import { MutationHookOptions } from "@apollo/client";
import { gql, useEnhancedMutation } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { useSafeTimeout } from "@uplift-ltd/use-safe-timeout";
import React, { useCallback, useEffect, useState } from "react";
import { SubscriptionDiscount } from "__generated__/globalTypes";
import { useAddToCartTracker } from "components/Cart/queries";
import { triggerPurchaseConversionTrackers } from "components/Cart/utils";
import { SubscriptionBilling } from "components/Subscriptions/subscriptionUtils";
import Alert from "components/common/Alert";
import Button from "components/common/Button";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import Loading, { LoadingButton } from "components/common/Loading";
import Modal, { ModalProps } from "components/common/Modal";
import { useDiscountCalculationsFromSubscription } from "components/common/Price/utils";
import { SUBSCRIPTION_AUTO_RENEWAL_TEXT } from "constants/site";
import { LOGIN_URL } from "constants/urls";
import { formatUSD } from "helpers/strings";
import useRouterNavigation from "hooks/useRouterNavigation";
import { useUserContext } from "hooks/useUserContext";
import { PurchaseCompleteProps } from "../../Cart/CheckoutModal";
import { StripeCheckout } from "../../Cart/StripeCheckout";
import DiscountCodeInput from "./DiscountCodeInput";
import { DiscountData } from "./DiscountCodeInput/DiscountCodeInput";
import {
  PurchaseProductMutation,
  PurchaseProductVariables,
} from "./__generated__/ProductPurchaseModal";
import styles from "./ProductPurchaseModal.module.css";

interface ProductPurchaseModalContentsProps extends PurchaseCompleteProps {
  onClose: () => void;
  productId?: string;
  productName?: string | null;
  productPrice?: number;
  subscriptionBilling?: SubscriptionBilling;
  hasPurchased: boolean;
  canPurchaseWithCredits: boolean;
  type: "course" | "bundle" | "credits" | "privatePracticeTool" | "event" | "subscription";
  creditCost?: number | null;
  redirectUrl?: string;
  refetchQueries?: MutationHookOptions["refetchQueries"];
  productVariationId?: string;
  subscriptionDiscount?: SubscriptionDiscount | null;
}

interface IsEnrolledModalContentProps {
  onClose: () => void;
}

const PURCHASE_PRODUCT = gql`
  mutation PurchaseProduct(
    $productId: ID!
    $couponCode: String
    $purchaseWithCredits: Boolean
    $productVariationId: ID
    $subscriptionId: ID
    $subscriptionProductFeatureId: ID
  ) {
    purchaseProduct(
      input: {
        productId: $productId
        purchaseWithCredits: $purchaseWithCredits
        couponCode: $couponCode
        productVariationId: $productVariationId
        subscriptionId: $subscriptionId
        subscriptionProductFeatureId: $subscriptionProductFeatureId
      }
    ) {
      success
      message
      errorCode
    }
  }
`;

const IsEnrolledModalContent: React.FC<IsEnrolledModalContentProps> = ({ onClose }) => {
  const setSafeTimeout = useSafeTimeout();

  useEffect(() => {
    setSafeTimeout(onClose, 2000);
  }, [onClose, setSafeTimeout]);

  return <div>You have successfully purchased the package.</div>;
};

const modelTitleMap: Record<ProductPurchaseModalContentsProps["type"], string> = {
  course: "Course Price",
  bundle: "Package Price",
  credits: "Credits Price",
  privatePracticeTool: "Product Price",
  event: "Webinar Price",
  subscription: "Subscription Price",
};

const ProductPurchaseModalContents = ({
  onClose,
  onPurchaseComplete,
  productId = "",
  productName = "",
  productPrice = 0,
  subscriptionBilling,
  hasPurchased = false,
  canPurchaseWithCredits = false,
  creditCost,
  type,
  redirectUrl,
  refetchQueries,
  productVariationId,
  subscriptionDiscount,
}: ProductPurchaseModalContentsProps) => {
  const { push } = useRouterNavigation();
  const [dialog, showDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponDiscountData, setCouponDiscountData] = useState<DiscountData>(() => ({
    validated: false,
    code: "",
    newPrice: 0,
  }));

  const user = useUserContext();
  const subscriptionDiscountInfo = useDiscountCalculationsFromSubscription({
    priceUSD: productPrice,
    userDiscountFromSubscription: subscriptionDiscount,
  });

  const userCredits = user.currentUser?.credits || 0;
  const variationId = productVariationId || null;
  const couponCode = couponDiscountData.code || null;
  const price =
    subscriptionDiscountInfo.hasNewPrice && subscriptionDiscountInfo.discountedPrice
      ? subscriptionDiscountInfo.discountedPrice
      : couponDiscountData.newPrice || productPrice;

  const canApplyCoupon = !!productPrice && !subscriptionDiscountInfo.isFreeOrDiscounted;

  const isDiscountedToZero =
    subscriptionDiscountInfo.isFree ||
    (couponDiscountData.validated && couponDiscountData.newPrice === 0);

  const canUseCredits =
    canPurchaseWithCredits && !isDiscountedToZero && !subscriptionDiscountInfo.isFreeOrDiscounted;

  const onCouponDiscountValidation = (data: DiscountData) => {
    setCouponDiscountData(data);
  };

  const [purchaseProduct, { loading }] = useEnhancedMutation<
    PurchaseProductMutation,
    PurchaseProductVariables
  >(PURCHASE_PRODUCT, { ...(refetchQueries && { refetchQueries }) });

  const redirect = useCallback(
    (url?: string) => {
      if (url) {
        push(url);
      }
    },
    [push]
  );

  const postNoPaymentPurchaseActions = () => {
    onClose();
    if (onPurchaseComplete) {
      onPurchaseComplete({ free: true });
    }
    triggerPurchaseConversionTrackers(0, "USD", "ZeroValueViaPurchaseNow", [
      parseInt(productId, 10),
    ]);
    redirect(redirectUrl);
  };

  const purchaseWithCredits = async () => {
    setError(null);

    const res = await purchaseProduct({
      variables: {
        productId,
        purchaseWithCredits: true,
        productVariationId,
      },
    });

    if (res.data?.purchaseProduct?.success) {
      postNoPaymentPurchaseActions();
    } else {
      setError(res.data?.purchaseProduct?.message || "Unknown error");
    }
  };

  const purchaseWithCouponOrZeroValue = async () => {
    setError(null);

    const res = await purchaseProduct({
      variables: {
        productId,
        couponCode: couponDiscountData.code,
        productVariationId,
        subscriptionId: subscriptionDiscount?.subscriptionId,
        subscriptionProductFeatureId: subscriptionDiscount?.productFeatureId,
      },
    });

    if (res.data?.purchaseProduct?.success) {
      postNoPaymentPurchaseActions();
    } else {
      setError(res.data?.purchaseProduct?.message || "Unknown error");
    }
  };

  const confirmPurchaseWithCredits = () => showDialog(true);
  const confirmAction = async () => {
    showDialog(false);
    await purchaseWithCredits();
  };

  if (subscriptionBilling?.interval === "UNKNOWN") {
    return <div>Subscription billing interval is unknown</div>;
  }

  if (type !== "credits" && hasPurchased) {
    return <IsEnrolledModalContent onClose={onClose} />;
  }

  if (!productId || user.loading) return <Loading />;
  if (type !== "credits" && hasPurchased) return <IsEnrolledModalContent onClose={onClose} />;

  return (
    <div className={styles.container}>
      <p className={styles.subtitle}>
        <strong>{productName}</strong>
      </p>
      {!isDiscountedToZero && (
        <p className={styles.subtitle}>Please make payment using one of the options below.</p>
      )}
      {canUseCredits && (
        <ConfirmationDialog
          open={dialog}
          loading={loading}
          onClose={() => showDialog(false)}
          onAccept={confirmAction}
        >
          <div>
            <p>
              Please confirm you would like to purchase <b>{productName}</b>
            </p>
            <p className="mt-2">
              Cost: <b>{creditCost} tokens</b>
            </p>
          </div>
        </ConfirmationDialog>
      )}
      {!!productPrice && (
        <>
          <div className={styles.priceBox}>
            <p className={styles.courseTitle}>{modelTitleMap[type]}:</p>
            <div className={styles.coursePrice}>
              {couponDiscountData.validated ? (
                <span>
                  <s>{formatUSD(productPrice)}</s>
                  <span className={styles.discountedPrice}>
                    {formatUSD(couponDiscountData.newPrice)}
                  </span>
                </span>
              ) : (
                <span>
                  <span
                    className={subscriptionDiscountInfo.isFreeOrDiscounted ? "line-through" : ""}
                  >
                    {formatUSD(productPrice)}
                  </span>
                  {subscriptionDiscountInfo.isFree && <span className="ml-1">FREE</span>}
                  {subscriptionDiscountInfo.hasNewPrice &&
                    subscriptionDiscountInfo.discountedPrice && (
                      <span className="ml-1">
                        {formatUSD(subscriptionDiscountInfo.discountedPrice)}
                      </span>
                    )}
                </span>
              )}
              {subscriptionBilling?.wording && (
                <span className={styles.subscriptionPeriod}> {subscriptionBilling.wording}</span>
              )}
            </div>
          </div>
          {subscriptionBilling?.wording && (
            <div className={styles.subscriptionRenewal}>{SUBSCRIPTION_AUTO_RENEWAL_TEXT}</div>
          )}
        </>
      )}
      {canUseCredits && Boolean(creditCost) && (
        <div className={styles.creditsBox}>
          <p className={styles.courseTitle}>Tokens Required: {creditCost}</p>
          <p className={styles.courseTitle}>Tokens Available: {userCredits}</p>
        </div>
      )}
      {canUseCredits && !isDiscountedToZero && (
        <>
          <Button
            disabled={!userCredits || !creditCost || userCredits < creditCost || loading}
            theme="primary"
            className="my-4 w-full"
            onClick={confirmPurchaseWithCredits}
          >
            {loading ? <LoadingButton /> : "Purchase with tokens"}
          </Button>
          <hr className={styles.divider} />
        </>
      )}
      {canApplyCoupon && (
        <DiscountCodeInput
          onValidate={onCouponDiscountValidation}
          productId={productId}
          productVariationId={productVariationId}
        />
      )}
      {isDiscountedToZero ? (
        <Button
          theme="primary"
          className="my-4 w-full"
          onClick={purchaseWithCouponOrZeroValue}
          disabled={loading}
        >
          {loading ? <LoadingButton /> : "Continue"}
        </Button>
      ) : (
        <>
          {!!productPrice && (
            <StripeCheckout
              value={price}
              productId={productId}
              productVariationId={variationId}
              couponCode={couponCode}
              subscriptionDiscount={subscriptionDiscount}
              setError={setError}
            />
          )}
        </>
      )}
      {error && (
        <Alert className="mt-2" theme="danger">
          {error}
        </Alert>
      )}
    </div>
  );
};

interface ProductPurchaseModalProps
  extends Pick<ModalProps, "open" | "onRequestClose">,
    Omit<ProductPurchaseModalContentsProps, "onClose">,
    PurchaseCompleteProps {
  title: string;
  WrapperComponent?: React.FC;
  productDatabaseId: number;
  subscriptionBilling?: SubscriptionBilling;
  subscriptionDiscount?: SubscriptionDiscount | null;
}

const ProductPurchaseModal = ({
  open,
  onRequestClose,
  onPurchaseComplete,
  productId = "",
  productName = "",
  productPrice = 0,
  hasPurchased = false,
  canPurchaseWithCredits = false,
  creditCost,
  redirectUrl,
  title,
  type,
  refetchQueries,
  productVariationId,
  productDatabaseId,
  WrapperComponent,
  subscriptionBilling,
  subscriptionDiscount,
}: ProductPurchaseModalProps) => {
  const user = useUserContext();
  const addToCartTracker = useAddToCartTracker();
  const { push } = useRouterNavigation();

  if (!user.isAuthenticated && open && typeof window !== "undefined") {
    push(
      makeUrl(LOGIN_URL, null, {
        nextPage: window.location.pathname,
      })
    );
  }

  if (open) {
    addToCartTracker(productDatabaseId);
  }

  let modalContents = (
    <ProductPurchaseModalContents
      productId={productId}
      productVariationId={productVariationId}
      productName={productName}
      productPrice={productPrice}
      subscriptionBilling={subscriptionBilling}
      creditCost={creditCost}
      hasPurchased={hasPurchased}
      canPurchaseWithCredits={canPurchaseWithCredits}
      type={type}
      onClose={onRequestClose}
      onPurchaseComplete={onPurchaseComplete}
      redirectUrl={redirectUrl}
      refetchQueries={refetchQueries}
      subscriptionDiscount={subscriptionDiscount}
    />
  );

  if (WrapperComponent) {
    modalContents = <WrapperComponent>{modalContents}</WrapperComponent>;
  }

  return (
    <Modal
      open={user.isAuthenticated ? open : false}
      onRequestClose={onRequestClose}
      title={title}
      requiresAuth
    >
      {modalContents}
    </Modal>
  );
};

export default ProductPurchaseModal;
